import {
    setBookmarkTextBox,
    attachStationMetaModalButton,
    attachElementMetaModalButton,
    scrollToOutput,
    renderInvalidStationGrowl,
    renderInvalidElementGrowl,
} from "../controller/home.js";
import {
    updateSelectedStationData,
    getStationDataElementsLength,
    getRefData,
    getSelectedStationMeta,
    defaultCentralTendency,
    getStationDataValues,
    getSelectedStationElement,
    parseApiReturnedDate,
} from "../model/get-data.js";
import { renderGrowl } from "@js/nwcc-growl.js";

export const awdbTableId = "awdb--table";

export async function makeTable(station, element) {
    const selectedStation = station || document.getElementById("select--station")?.value;
    const selectedElement = element || document.getElementById("select--element")?.value;
    if (!selectedStation) {
        renderInvalidStationGrowl();
    } else if (!selectedElement) {
        renderInvalidElementGrowl();
    } else {
        await updateSelectedStationData();
        updateTable();
        scrollToOutput();
        setBookmarkTextBox();
    }
}

export function getStationElementBooleans() {
    const elementBools = {};
    const centralTendency = defaultCentralTendency.toLowerCase();
    const dataTypes = ["value", "qcFlag", centralTendency, "origValue"];
    const numElements = getStationDataElementsLength();
    for (let elemIdx = 0; elemIdx < numElements; elemIdx++) {
        const values = getStationDataValues(elemIdx);
        const stationElement = getSelectedStationElement(elemIdx);
        elementBools[stationElement.elementCode] = [];
        dataTypes.forEach(function (dataType) {
            if (values.some((value) => dataType in value)) {
                const existingBool = elementBools[stationElement.elementCode];
                existingBool.push(dataType);
                elementBools[stationElement.elementCode] = existingBool;
            }
        });
    }
    return elementBools;
}

export function joinStationElementsData() {
    const joinedData = {};
    const numElements = getStationDataElementsLength();
    for (let elemIdx = 0; elemIdx < numElements; elemIdx++) {
        const values = getStationDataValues(elemIdx);
        const stationElement = getSelectedStationElement(elemIdx);
        const duration = stationElement.durationName;
        values.forEach(function (data) {
            const date = parseApiReturnedDate(data, duration);
            const existingEntry = joinedData[date] || [];
            existingEntry.push(data);
            joinedData[date] = existingEntry;
            if (joinedData[date].length != elemIdx + 1) {
                const missingValue = { ...data };
                Object.keys(missingValue).forEach(function (entryType) {
                    if (entryType != "date") {
                        missingValue[entryType] = "--";
                    }
                });
                existingEntry.splice(elemIdx - 1, 0, missingValue);
            }
        });
    }
    Object.keys(joinedData).forEach((date) => {
        if (joinedData[date].length < numElements) {
            const numMissing = numElements - joinedData[date].length;
            const missingFiller = { date: date };
            const missingValues = Array.from({ length: numMissing }, () => ({ ...missingFiller }));
            joinedData[date] = [...joinedData[date], ...missingValues];
        }
    });
    const dateEntries = Object.entries(joinedData);
    dateEntries.sort((a, b) => {
        return a[0].localeCompare(b[0]);
    });
    return Object.fromEntries(dateEntries);
}

export function updateTable(newestToOldest = true, tableId = awdbTableId) {
    const elementLookUp = getRefData().elements;
    const numElements = getStationDataElementsLength();
    if (numElements === 0) {
        renderGrowl(
            "No data returned!",
            "No data was found for the request, please try a different query",
            "error",
            true,
            10,
        );
    }
    const breakPoint = numElements < 3 ? " fsa-grid__1/2@l" : "";
    const awdbGrid = document.getElementById("awdb--grid");
    awdbGrid.className = `fsa-grid__1/1${breakPoint} fsa-p--s`;
    const stationMeta = getSelectedStationMeta();
    const outputDiv = document.getElementById("awdb--output");
    outputDiv.innerHTML = "";
    const table = document.createElement("table");
    table.id = tableId;
    table.className =
        "fsa-table fsa-table--borderless fsa-table--valign-bottom fsa-table--responsive fsa-table--responsive-horizontal";
    const caption = document.createElement("caption");
    caption.innerHTML = `${stationMeta.name} (${stationMeta.stationTriplet})`;
    table.appendChild(caption);
    const tHead = document.createElement("thead");
    const headTr = document.createElement("tr");
    const headThDate = document.createElement("th");
    headThDate.className = "fsa-text-align--left fsa-table__th--sticky";
    headThDate.scope = "col";
    headThDate.innerHTML = "Date";
    headTr.appendChild(headThDate);
    const tBody = document.createElement("tbody");
    const elementBools = getStationElementBooleans();
    const joinedData = joinStationElementsData();

    tHead.appendChild(headTr);
    table.appendChild(tHead);
    const headerLabels = [];
    const joinedDataDates = Object.keys(joinedData);
    if (!newestToOldest) {
        joinedDataDates.reverse();
    }
    joinedDataDates.forEach(function (date) {
        const tr = document.createElement("tr");
        const dateTd = document.createElement("td");
        dateTd.ariaLabel = "Date";
        dateTd.innerHTML = date;
        tr.appendChild(dateTd);
        joinedData[date].forEach(function (value, elementIdx) {
            const valTd = document.createElement("td");
            const stationElement = getSelectedStationElement(elementIdx);
            const depth = stationElement.heightDepth;
            const elementLabel = depth
                ? `${stationElement.elementCode} (${stationElement.storedUnitCode}) ${depth}"`
                : `${stationElement.elementCode} (${stationElement.storedUnitCode})`;
            valTd.ariaLabel = elementLabel;
            valTd.className = value.qcFlag === "S" ? "fsa-text-align--right fsa-color--red" : "fsa-text-align--right";
            valTd.innerHTML = value.value || value.value === 0 ? value.value : "--";
            tr.appendChild(valTd);
            if (!headerLabels.includes(elementLabel)) {
                const headThElement = document.createElement("th");
                headThElement.scope = "col";
                headThElement.className = "fsa-text-align--right fsa-table__th--sticky";
                headThElement.innerHTML = elementLabel;
                headThElement.title = elementLookUp[stationElement.elementCode].name;
                headTr.appendChild(headThElement);
                headerLabels.push(elementLabel);
            }
            if (elementBools[stationElement.elementCode].includes("median")) {
                const medTd = document.createElement("td");
                const medianLabel = `Median ${elementLabel}`;
                medTd.ariaLabel = medianLabel;
                medTd.className = "fsa-text-align--right";
                medTd.innerHTML = value.median || value.median === 0 ? value.median : "--";
                tr.appendChild(medTd);
                tBody.appendChild(tr);
                if (!headerLabels.includes(medianLabel)) {
                    const headThMedian = document.createElement("th");
                    headThMedian.scope = "col";
                    headThMedian.className = "fsa-text-align--right fsa-table__th--sticky";
                    headThMedian.innerHTML = medianLabel;
                    headThMedian.title = `MEDIAN ${elementLookUp[stationElement.elementCode].name}`;
                    headTr.appendChild(headThMedian);
                    headerLabels.push(medianLabel);
                }
            }
            if (elementBools[stationElement.elementCode].includes("qcFlag")) {
                const flagTd = document.createElement("td");
                const flagLabel = `QC Flag ${elementLabel}`;
                flagTd.ariaLabel = flagLabel;
                flagTd.className =
                    value.qcFlag === "S" ? "fsa-text-align--right fsa-color--red" : "fsa-text-align--right";
                flagTd.innerHTML = value.qcFlag || value.qcFlag === 0 ? value.qcFlag : "--";
                tr.appendChild(flagTd);
                tBody.appendChild(tr);
                if (!headerLabels.includes(flagLabel)) {
                    const flagTh = document.createElement("th");
                    flagTh.scope = "col";
                    flagTh.className = "fsa-text-align--right fsa-table__th--sticky";
                    flagTh.innerHTML = flagLabel;
                    flagTh.title = `QC FLAG ${elementLookUp[stationElement.elementCode].name}`;
                    headTr.appendChild(flagTh);
                    headerLabels.push(flagLabel);
                }
            }
            if (elementBools[stationElement.elementCode].includes("origValue")) {
                const origTd = document.createElement("td");
                const origLabel = `Orig. ${elementLabel}`;
                origTd.ariaLabel = origLabel;
                origTd.className = "fsa-text-align--right fsa-bg--yellow-100";
                origTd.innerHTML = value.origValue || value.origValue === 0 ? value.origValue : "--";
                tr.appendChild(origTd);
                tBody.appendChild(tr);
                if (!headerLabels.includes(origLabel)) {
                    const origTh = document.createElement("th");
                    origTh.scope = "col";
                    origTh.className = "fsa-text-align--right fsa-table__th--sticky";
                    origTh.innerHTML = origLabel;
                    origTh.title = `ORIGINAL ${elementLookUp[stationElement.elementCode].name}`;
                    headTr.appendChild(origTh);
                    headerLabels.push(origLabel);
                }
            }
        });

        tBody.appendChild(tr);
    });
    table.appendChild(tBody);
    outputDiv.appendChild(table);
    addTableControls(outputDiv, newestToOldest);
}

export function addTableControls(outputDiv, newestToOldest) {
    const ctrlBox = document.createElement("div");
    ctrlBox.className = "fsa-box@l fsa-box--br-2 fsa-m--xxs fsa-p--xxs";
    const tblControls = document.createElement("div");
    tblControls.className = "fsa-level fsa-level--justify-between";
    ctrlBox.appendChild(tblControls);
    outputDiv.insertBefore(ctrlBox, outputDiv.firstChild);
    attachStationMetaModalButton(tblControls);
    attachElementMetaModalButton(tblControls);
    const reverseOrderSpan = document.createElement("span");
    const reverseOrderCb = document.createElement("input");
    reverseOrderCb.className = "fsa-checkbox";
    reverseOrderCb.id = "cb--reverse-table";
    reverseOrderCb.type = "checkbox";
    reverseOrderCb.checked = newestToOldest;
    reverseOrderCb.name = "cb--reverse-table";
    reverseOrderSpan.appendChild(reverseOrderCb);
    const reverseOrderLabel = document.createElement("label");
    reverseOrderLabel.for = "cb--reverse-table";
    reverseOrderLabel.innerHTML = "Oldest to Newest";
    reverseOrderSpan.appendChild(reverseOrderLabel);
    tblControls.appendChild(reverseOrderSpan);
    reverseOrderSpan.addEventListener("click", (event) => {
        reverseOrderCb.checked = !reverseOrderCb.checked;
        updateTable(reverseOrderCb.checked);
    });
}
